import { useState, type FormEvent, type ReactNode } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import { useMutation } from '@tanstack/react-query';
import { Banner, Btn, Field, NestLogo, ProductBadge, TextInput, v } from '@infinix/ui';
import { returnAfterSignIn } from '@infinix/registry';
import { accountApi } from './account';
import { ApiError } from './api';
import { useSession, type EndReason } from './session';

/**
 * Sign-in for every Nest app (decision A-25): one Nest account, one session. The account backend signs in against
 * Core; afterwards the person returns to the page that sent them here, or lands on their app (A-11). Forgot / reset
 * password use Core's email flow. Core OIDC with MFA replaces the password form when Core ships it (plan Deferred).
 */

/**
 * Where to return after sign-in (a deep link the person opened before signing in). Only a path on this origin outside
 * the sign-in pages is honoured: parsed as a URL (browsers drop tabs and newlines, so "/\t/evil.com" would become
 * "//evil.com"), rejected if it has control characters or resolves to another origin.
 */
export function safeNext(raw: string | null): string | null {
  // eslint-disable-next-line no-control-regex -- deliberately rejects control characters (open-redirect guard)
  if (!raw || !raw.startsWith('/') || /[\u0000-\u001f\u007f\\]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || url.pathname.startsWith('/login')) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

const ENDED: Record<EndReason, string> = {
  idle: 'You were signed out after 30 minutes without activity.',
  expired: 'Your session ended. Sign in again to continue.',
  'signed-out': "You've signed out.",
};

function Layout({ title, intro, children }: { title: string; intro?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '52% 48%' }, background: v('bg') }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'space-between', minHeight: '100dvh',
          p: '44px 56px', background: v('brand-50'), borderRight: `1px solid ${v('railBorder')}`,
        }}
      >
        <NestLogo size={38} />
        <Box>
          <Box component="h1" sx={{ m: 0, fontSize: 40, lineHeight: 1.12, fontWeight: 600, letterSpacing: '-0.025em', maxWidth: 520 }}>
            Every payment, every product, one place to run it.
          </Box>
          <Box sx={{ mt: '16px', fontSize: 15.5, color: v('dim'), maxWidth: 500, lineHeight: 1.6 }}>
            Payments, your organization and every Nest product, with one account. What you see depends on your role.
          </Box>
          <Box sx={{ mt: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(['SmartBill', 'Retail', 'Giving', 'Stay'] as const).map((p) => <ProductBadge key={p} name={p} />)}
          </Box>
        </Box>
        <Box sx={{ fontSize: 12.5, color: v('caption') }}>© 2026 Nest · Access is scoped to your organization and business units.</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: '32px 20px', md: '44px' } }}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: '28px' }}><NestLogo size={32} /></Box>
          <Box component="h2" sx={{ m: 0, fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}>{title}</Box>
          {intro && <Box sx={{ mt: '8px', fontSize: 14, color: v('dim'), lineHeight: 1.55 }}>{intro}</Box>}
          <Box sx={{ mt: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}

const linkSx = { color: v('accentText'), fontWeight: 600, fontSize: 13, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } } as const;

export function SignIn() {
  const s = useSession();
  const nav = useNavigate();
  // A session starts fresh: a page that named an organization or unit isn't reopened (staff land in Ops).
  const next = returnAfterSignIn(safeNext(new URLSearchParams(window.location.search).get('next')));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const signIn = useMutation({
    mutationFn: () => accountApi.signIn(email.trim(), password),
    onSuccess: (view) => {
      s.signedIn(view);
      // Another app's page: a full page load into that deployable; otherwise the landing picks the app.
      if (next) window.location.assign(next);
      else nav('/', { replace: true });
    },
    onError: () => setPassword(''),
  });
  if (s.status === 'signed-in' && !signIn.isSuccess) return <Navigate to={next ?? '/'} replace />;

  const err = signIn.error instanceof ApiError ? signIn.error : null;
  const message = err ? (err.code === 'VALIDATION_FAILED' ? 'Enter your email and password.' : err.message) : signIn.isError ? 'Something went wrong. Try again.' : null;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!signIn.isPending && email && password) signIn.mutate();
  };

  return (
    <Layout title="Sign in" intro="Use your Nest account.">
      {s.endReason && !signIn.isError && <Banner tone="info">{ENDED[s.endReason]}</Banner>}
      {message && <Banner tone="danger">{message}</Banner>}
      <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Field label="Work email" htmlFor="signin-email" required>
          <TextInput id="signin-email" type="email" value={email} onChange={setEmail} autoFocus />
        </Field>
        <Field label="Password" htmlFor="signin-password" required>
          <TextInput id="signin-password" type="password" value={password} onChange={setPassword} />
        </Field>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '-4px' }}>
          <Box component={RouterLink} to="/login/forgot" sx={linkSx}>Forgot password?</Box>
        </Box>
        <Btn kind="primary" type="submit" disabled={!email || !password || signIn.isPending}>{signIn.isPending ? 'Signing in…' : 'Sign in'}</Btn>
      </Box>
      <Box sx={{ fontSize: 12.5, color: v('caption') }}>Access is granted by your organization's admin.</Box>
    </Layout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const send = useMutation({ mutationFn: () => accountApi.forgotPassword(email.trim()) });
  const err = send.error instanceof ApiError ? send.error : null;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!send.isPending && email) send.mutate();
  };
  return (
    <Layout title="Reset your password" intro="We'll email you a link to choose a new password.">
      {send.isSuccess ? (
        <Banner tone="success">{send.data.message}</Banner>
      ) : (
        <>
          {err && <Banner tone="danger">{err.code === 'VALIDATION_FAILED' ? 'Enter your work email.' : err.message}</Banner>}
          <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Field label="Work email" htmlFor="forgot-email" required>
              <TextInput id="forgot-email" type="email" value={email} onChange={setEmail} autoFocus />
            </Field>
            <Btn kind="primary" type="submit" disabled={!email || send.isPending}>{send.isPending ? 'Sending…' : 'Send reset link'}</Btn>
          </Box>
        </>
      )}
      <Box component={RouterLink} to="/login" sx={linkSx}>Back to sign in</Box>
    </Layout>
  );
}

/** The reset link from Core's email: the code arrives in the URL fragment, which is never sent to a server or logged. */
export function ResetPassword() {
  const code = new URLSearchParams(window.location.hash.slice(1)).get('code') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const reset = useMutation({ mutationFn: () => accountApi.resetPassword(code, password) });
  const err = reset.error instanceof ApiError ? reset.error : null;
  const mismatch = confirm.length > 0 && confirm !== password;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!reset.isPending && code && password && password === confirm) reset.mutate();
  };
  if (!code) {
    return (
      <Layout title="Reset your password">
        <Banner tone="danger">This reset link is incomplete. Open the link from the email again, or ask for a new one.</Banner>
        <Box component={RouterLink} to="/login/forgot" sx={linkSx}>Ask for a new link</Box>
      </Layout>
    );
  }
  return (
    <Layout title="Choose a new password">
      {reset.isSuccess ? (
        <>
          <Banner tone="success">Your password has been changed. Sign in with your new password.</Banner>
          <Box component={RouterLink} to="/login" sx={linkSx}>Sign in</Box>
        </>
      ) : (
        <>
          {err && <Banner tone="danger">{err.fields._ ?? err.message}</Banner>}
          <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Field label="New password" htmlFor="reset-password" required>
              <TextInput id="reset-password" type="password" value={password} onChange={setPassword} autoFocus />
            </Field>
            <Field label="Confirm new password" htmlFor="reset-confirm" required error={mismatch ? "Passwords don't match." : undefined}>
              <TextInput id="reset-confirm" type="password" value={confirm} onChange={setConfirm} invalid={mismatch} />
            </Field>
            <Btn kind="primary" type="submit" disabled={!password || password !== confirm || reset.isPending}>{reset.isPending ? 'Saving…' : 'Change password'}</Btn>
          </Box>
        </>
      )}
    </Layout>
  );
}
